import React, { useState, useEffect } from "react";
import { Row, Button, Modal, Form } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";

import { URLS } from "../../constants";
import { useModal } from "../../hooks";
import { UserAddForm, UserEditForm } from "../../components/Forms";
import { ContentInner, StyledTable } from "../styled";

export default function Users({ token }) {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [newUser, setNewUser] = useState({});
  const [currentUser, setCurrentUser] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);

  const initialValues = { name: "", role: "", email: "", password: "" };
  const [isModalShown, setModalShown, onModalOkAction, onModalCancelAction] =
    useModal();
  const [
    isEditModalShown,
    setEditModalShown,
    onEditModalOkAction,
    onEditModalCancelAction,
  ] = useModal();
  const [
    isDeleteModalShown,
    setDeleteModalShown,
    onDeleteModalOkAction,
    onDeleteModalCancelAction,
  ] = useModal();

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 250,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      align: "center",
      with: 110,
      render: (_, row) =>
        row.role === "super_admin" ? "Super Admin" : "Company Admin",
    },
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
      width: 72,
      render: (_, row) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => {
              onEditUser(row);
            }}
          >
            Edit
          </Button>
        );
      },
    },
    {
      title: "",
      dataIndex: "delete",
      key: "delete",
      width: 88,
      render: (_, row) => {
        return (
          <Button
            danger
            type="link"
            size="small"
            onClick={() => {
              setItemToDelete(row);
              setDeleteModalShown()();
            }}
          >
            Delete
          </Button>
        );
      },
    },
  ];

  const getUsers = async () => {
    axios
      .get(URLS.FETCH_USERS, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setData(res.data);
      });
  };

  const deleteUserById = async () => {
    return axios
      .delete(`${URLS.FETCH_USERS}/${itemToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => res.data);
  };

  const onDelete = async () => {
    const response = await deleteUserById();

    if (response) {
      const newData = data.filter((d) => {
        return d._id !== itemToDelete._id;
      });

      setData(newData);
    }
  };

  const handleOk = () => {
    onDelete(itemToDelete);
    onDeleteModalOkAction()();
  };

  const onEditUser = (data) => {
    setCurrentUser({ ...data });
    setEditModalShown()();
  };

  const updateUser = (name, value, isNew) => {
    if (isNew) {
      setNewUser({ ...newUser, [name]: value });
    } else {
      setCurrentUser({ ...currentUser, [name]: value });
    }
  };

  const onInputChange = (isNew) => (e) => {
    const { name, value } = e.target;
    updateUser(name, value, isNew);
  };

  const resetCurrentUser = () => {
    setCurrentUser(initialValues);
  };

  const showCreateModal = () => {
    resetCurrentUser();
    setModalShown()();
  };

  const createUser = async () => (
    axios
    .post(URLS.FETCH_USERS, newUser, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => res.data)
  );

  const editUser = async () => (
    axios
    .put(`${URLS.FETCH_USERS}/${currentUser._id}`, currentUser, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => res.data)
  );

  const onFinishAdd = async () => {
    onModalOkAction()();
    const response = await createUser();

    if (response) {
      setNewUser({});
      setData([...data, response])
    }
  };

  const onFinishEdit = async () => {
    onEditModalOkAction()();
    const response = await editUser();

    if (response) {
      const newData = data.map((item) => {
        if (response._id === item._id) {
          return {...response};
        }
        return item;
      });
      setData(newData);
    }
  };

  useEffect(() => {
    if (!data.length) {
      getUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return (
    <>
      <Modal
        title="Delete user"
        visible={isDeleteModalShown}
        onOk={handleOk}
        onCancel={onDeleteModalCancelAction()}
      >
        Are you sure you want to delete user?
      </Modal>
      <Modal
        title="Create new user"
        okText="Create user"
        visible={isModalShown}
        onOk={() => {}}
        onCancel={() => {
          onModalCancelAction()();
        }}
        okButtonProps={{
          form: "add-user-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <UserAddForm
          user={newUser}
          form={form}
          onFinish={onFinishAdd}
          onInputChange={onInputChange(true)}
          onRoleChange={(value) => {
            updateUser("role", value, true);
          }}
        />
      </Modal>

      <Modal
        title="Edit user"
        okText="Save changes"
        visible={isEditModalShown}
        onOk={() => {}}
        onCancel={() => {
          onEditModalCancelAction()();
          setCurrentUser({});
        }}
        okButtonProps={{
          form: "edit-user-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <UserEditForm
          user={currentUser}
          form={form}
          onFinish={onFinishEdit}
          onInputChange={onInputChange()}
          onRoleChange={(value) => {
            updateUser("role", value);
          }}
        />
      </Modal>
      <ContentInner>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => {
            showCreateModal();
          }}
          style={{ marginBottom: "25px" }}
        >
          Add new user
        </Button>
        <Row justify="center" width="100%">
          <StyledTable
            style={{ width: "100%" }}
            columns={columns}
            rowKey="_id"
            dataSource={data}
            pagination={false}
            tableLayout="fixed"
          />
        </Row>
      </ContentInner>
    </>
  );
}
